import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import "./style.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import AxiosInstance from "../../AxiosInstance";
import { postFile } from "../../../components/Files/Functions";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import AddInvoice from "./Views/AddInvoice";
import showToast from "../../../components/Toast/Toster";
import { handleAuth } from "../../../components/Login/Auth";
import { useUserType } from "../../../contexts/UserTypeContext";
import { getItem } from "../../../utils/storage";
import {
  normalizeInvoicePaymentDue,
  netTermDays,
  INVOICE_PAYMENT_DUE_CUSTOM,
} from "../../../constants/invoicePaymentTerms";

/** Compare line-item images: API returns string filename; edits may use a new File. */
function lineItemAttachmentKey(att) {
  if (att == null || att === "") return "";
  if (typeof att === "string") return att;
  if (typeof File !== "undefined" && att instanceof File) {
    return `file:${att.name}:${att.size}:${att.lastModified}`;
  }
  if (typeof Blob !== "undefined" && att instanceof Blob) {
    return `blob:${att.size}`;
  }
  return String(att);
}

/**
 * Deep snapshot of line items for "original" baseline (same idea as Addquotes setInitialLineItems).
 * Without this, originalLineItems shares object references with lineItems; GetMaterialsAndLabor
 * mutates item.Attachment in place, so the "original" attachment changes too and no diff is detected.
 */
function deepSnapshotLineItems(items) {
  if (!items?.length) return [];
  try {
    return JSON.parse(JSON.stringify(items));
  } catch (e) {
    console.warn("deepSnapshotLineItems: falling back to shallow copy", e);
    return items.map((item) => ({ ...item }));
  }
}

function addDaysFromIssueDateString(issueDateStr, daysToAdd) {
  if (!Number.isFinite(daysToAdd) || daysToAdd <= 0) {
    const d = new Date();
    return d.toISOString().split("T")[0];
  }
  if (!issueDateStr) {
    const d = new Date();
    d.setDate(d.getDate() + daysToAdd);
    return d.toISOString().split("T")[0];
  }
  const m = moment(
    issueDateStr,
    [moment.ISO_8601, "YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YYYY"],
    true
  );
  if (!m.isValid()) {
    const d = new Date();
    d.setDate(d.getDate() + daysToAdd);
    return d.toISOString().split("T")[0];
  }
  return m.clone().add(daysToAdd, "days").format("YYYY-MM-DD");
}

const InvoiceDetails = () => {
  const navigate = useNavigate();
  const { CompanyUrl } = useParams();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const formatDateWithUserTimezone = useUserTimezoneFormatter();
  const location = useLocation();
  const [lineItems, setLineItems] = useState([
    {
      Description: "",
      Name: "",
      Attachment: "",
      Type: "",
      Unit: "",
      CostPerUnit: "",
      Hourly: "",
      CostPerHour: "",
      Fixed: "",
      CostPerFixed: "",
      Square: "",
      CostPerSquare: "",
      Total: "",
      isNew: true,
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [DueDate, setDueDate] = useState("");
  const [IssueDate, setIssueDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [isNumChange, setIsNumChange] = useState(false);
  const [tokenDecode, setTokenDecode] = useState({});
  const [invoiceData, setInvoiceData] = useState(null);
  const [isCollect, setIsCollect] = useState(false);
  const [shouldOpenEmailAfterSave, setShouldOpenEmailAfterSave] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showCosts, setShowCosts] = useState(
    new Array(lineItems.length).fill(false)
  );
  const [originalFormData, setOriginalFormData] = useState(null);
  const [originalLineItems, setOriginalLineItems] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touchedLineItems, setTouchedLineItems] = useState({}); // Track touched fields per line item

  const [selectedVisits, setSelectedVisits] = useState([]);
  const appliedInvoiceSettingsDefaults = useRef(false);

  // Card fee configuration (needed for New York surcharge: hide Discount line and
  // bake the surcharge % into the invoice Total so customers see the card-price
  // total at checkout, matching NY surcharge compliance requirements).
  const [cardFeeConfig, setCardFeeConfig] = useState({
    PricingStructure: "Cash Discount",
    FeeLabel: "",
    FeeType: "",
    FeeAmount: 0,
  });
  // NY merchants can configure a customer-facing disclaimer (Settings → Disclaimer)
  // stored on UserProfile.SurchargeDisclaimer. When set, we render it below the
  // Contract / Disclaimer on the invoice.
  const [surchargeDisclaimer, setSurchargeDisclaimer] = useState("");

  const fetchDatas = async () => {
    try {
      const res = await handleAuth(navigate, location);
      setTokenDecode(res?.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  useEffect(() => {
    fetchDatas();
  }, []);

  // Load merchant's card-fee config to determine pricing structure.
  useEffect(() => {
    const companyId = getItem("CompanyId");
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await AxiosInstance.get(
          `/v1/card-fee/company/${companyId}`
        );
        if (cancelled) return;
        if (response?.data?.statusCode === 200 && response?.data?.data) {
          setCardFeeConfig(response.data.data);
        }
      } catch (error) {
        // Fall back to Cash Discount defaults on error; don't block invoice creation.
        console.error("Error fetching card fee config (AddInvoice):", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load merchant's SurchargeDisclaimer (Settings → Disclaimer). When set, it is
  // rendered below the Contract / Disclaimer on the invoice.
  useEffect(() => {
    const companyId = getItem("CompanyId");
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await AxiosInstance.get(
          `/v1/user/company-profile/${companyId}`
        );
        if (cancelled) return;
        if (res?.data?.success) {
          const text = res?.data?.data?.userProfile?.SurchargeDisclaimer || "";
          setSurchargeDisclaimer(text);
        }
      } catch (error) {
        console.error("Error fetching surcharge disclaimer:", error?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [menuIsOpen, setMenuIsOpen] = useState(
    new Array(lineItems?.length).fill(false)
  );

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const formik = useFormik({
    initialValues: {
      Subject: "",
      Description: "",
      InvoiceNumber: 1,
      Message: "",
      CustomerId: "",
      CompanyId: getItem("CompanyId"),
      LocationId: "",
      Notes: "",
      ContractDisclaimer:
        "Contract / Disclaimer\nThis invoice is valid for 30 days from the issue date. After this period, prices and terms may be subject to change.",
      Attachment: [],
      selectedTeams: [],
      Discount: "",
      Tax: "",
      CustomDays: "",
      PaymentDue: "Upon Receipt",
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      Subject: Yup.string().required("Subject required"),
      InvoiceNumber: Yup.number()
        .required("Invoice Number is required")
        .positive("Invoice Number must be a positive number")
        .integer("Invoice Number must be a whole number")
        .typeError("Invoice Number must be a valid number"),
    }),
    onSubmit: async (values, { setFieldTouched, validateForm, setErrors } = {}) => {
      setSubmitAttempted(true);
      
      // Touch all required fields
      setFieldTouched("Subject", true);
      setFieldTouched("InvoiceNumber", true);

      // Check for empty line items and mark them as touched to show red boxes
      const emptyLineItemIndices = [];
      lineItems.forEach((item, index) => {
        if (!item?.Name || item?.Name.trim() === "") {
          emptyLineItemIndices.push(index);
          // Mark the Name field as touched for this line item
          setTouchedLineItems(prev => ({
            ...prev,
            [index]: {
              ...prev[index],
              Name: true
            }
          }));
        }
      });

      // If there are empty line items, stop submission and show error
      if (emptyLineItemIndices.length > 0) {
        showToast.error("Please select material or labor for all line items before saving.");
        setErrors({});
        setLoading(false);
        return;
      }

      // Wait for validation to complete
      await new Promise(resolve => setTimeout(resolve, 0));
      const validationErrors = await validateForm();

      // Ensure errors are set for all empty required fields
      const errorsToSet = { ...validationErrors };
      
      if (!values.Subject || values.Subject.trim() === "") {
        errorsToSet.Subject = errorsToSet.Subject || "Subject required";
      }
      if (!values.InvoiceNumber || values.InvoiceNumber <= 0) {
        errorsToSet.InvoiceNumber = errorsToSet.InvoiceNumber || "Invoice Number is required";
      }

      // If there are validation errors, stop submission
      if (Object.keys(errorsToSet).length > 0) {
        setErrors(errorsToSet);
        setLoading(false);
        return;
      }

      try {
        if (
          location?.state?.invoiceId &&
          originalFormData &&
          originalLineItems
        ) {
          const currentFormData = {
            Subject: values?.Subject,
            Message: values?.Message,
            Notes: values?.Notes,
            ContractDisclaimer: values?.ContractDisclaimer,
            Discount: values?.Discount,
            Tax: values?.Tax,
            PaymentDue: values?.PaymentDue,
            CustomDays: values?.CustomDays,
            Attachment: values?.Attachment,
          };

          const hasFormChanges = Object.keys(currentFormData).some((key) => {
            if (key === "Attachment") {
              const currentAttachments = currentFormData[key] || [];
              const originalAttachments = originalFormData[key] || [];

              if (currentAttachments.length !== originalAttachments.length) {
                return true;
              }

              return currentAttachments.some((currentItem, index) => {
                const originalItem = originalAttachments[index];
                if (
                  typeof currentItem === "object" &&
                  typeof originalItem === "string"
                ) {
                  return true;
                }
                if (
                  typeof currentItem === "string" &&
                  typeof originalItem === "string"
                ) {
                  return currentItem !== originalItem;
                }
                if (
                  typeof currentItem === "string" &&
                  typeof originalItem === "object"
                ) {
                  return true;
                }
                return false;
              });
            }
            return currentFormData[key] !== originalFormData[key];
          });

          const hasLineItemChanges =
            lineItems?.length !== originalLineItems?.length ||
            lineItems?.some((item, index) => {
              const originalItem = originalLineItems[index];
              if (!originalItem) return true;

              return (
                item?.Name !== originalItem?.Name ||
                item.Description !== originalItem.Description ||
                item?.Type !== originalItem?.Type ||
                item?.Unit !== originalItem?.Unit ||
                item?.CostPerUnit !== originalItem?.CostPerUnit ||
                item?.Hourly !== originalItem?.Hourly ||
                item?.CostPerHour !== originalItem?.CostPerHour ||
                item?.Fixed !== originalItem?.Fixed ||
                item?.CostPerFixed !== originalItem?.CostPerFixed ||
                item?.Square !== originalItem?.Square ||
                item?.CostPerSquare !== originalItem?.CostPerSquare ||
                item?.Total !== originalItem?.Total ||
                lineItemAttachmentKey(item?.Attachment) !==
                  lineItemAttachmentKey(originalItem?.Attachment)
              );
            });

          if (!hasFormChanges && !hasLineItemChanges) {
            showToast.warning(
              "Please make changes to any field before submitting"
            );
            setLoading(false);
            return;
          }
        }

        // Validate invoice number before saving
        const invoiceNumber = values?.InvoiceNumber;
        if (!invoiceNumber || invoiceNumber <= 0) {
          showToast.error("Please enter a valid invoice number.");
          setLoading(false);
          setLoader(false);
          return;
        }

        // Check if invoice number exists (only if number was changed or it's a new invoice)
        const companyId = getItem("CompanyId") || tokenDecode?.companyId;
        const invoiceId = location?.state?.invoiceId;
        const isNewInvoice = !invoiceId;
        const invoiceNumberChanged = invoiceId && 
          String(invoiceNumber) !== String(invoiceData?.InvoiceNumber);

        if (isNewInvoice || invoiceNumberChanged) {
          try {
            const checkPayload = {
              InvoiceNumber: Number(invoiceNumber),
            };
            
            // If editing, exclude current invoice from check
            if (invoiceId) {
              checkPayload.InvoiceId = invoiceId;
            }

            const checkRes = await AxiosInstance.post(`/v1/invoice/check_number`, checkPayload);

            if (checkRes?.data?.statusCode === 203) {
              showToast.error(
                "Invoice number already exists. Please choose a different number."
              );
              setLoading(false);
              setLoader(false);
              return;
            }
          } catch (error) {
            if (error?.response?.data?.statusCode === 203) {
              showToast.error(
                "Invoice number already exists. Please choose a different number."
              );
              setLoading(false);
              setLoader(false);
              return;
            }
            // If it's a different error, continue with save (backend will also validate)
          }
        }

        setLoading(true);
        const strings = [];
        for (let item of values?.Attachment) {
          if (typeof item !== "string") {
            const string = await postFile(item);
            strings.push(string);
          } else {
            strings.push(item);
          }
        }
        // Filter out empty line items (items without Name/ProductId selected)
        const validLineItems = (lineItems || []).filter(item => 
          item?.Name && item?.Name.trim() !== "" && 
          (item?.ProductId || item?.Name)
        );

        // Validate that there is at least one valid line item
        if (validLineItems.length === 0) {
          showToast.error("Please select at least one material or labor item before saving.");
          setLoading(false);
          setLoader(false);
          return;
        }

        for (let item of validLineItems) {
          if (typeof item?.Attachment !== "string") {
            const string = await postFile(item?.Attachment);
            item.Attachment = string;
          }
        }
        // 🔍 DEBUG: Track what's being saved
        

        const object = {
          ...values,
          items: validLineItems,
          subTotal: validLineItems?.reduce(
            (sum, item) => Number(sum) + Number(item?.Total || 0) || 0,
            0
          ),
          Total: Total,
          Attachment: strings,
          Role: tokenDecode,
          IssueDate: IssueDate,
          DueDate: DueDate,
          ContractId: location?.state?.id,
          AddedAt: new Date(),
          CompanyId:
            getItem("CompanyId") || tokenDecode?.companyId,
          selectedVisits: location?.state?.selectedVisits || [],
          // Include tax calculation details
          MaterialTotal: calculatedTaxes.materialSubTotal,
          LaborTotal: calculatedTaxes.laborSubTotal,
          TaxableAmount: discountedTotal,
          TotalTax: calculatedTaxes.totalTaxes,
          CalculatedTaxes: calculatedTaxes.taxDetails.map(tax => ({
            ...tax,
            appliedTo: tax.type === "Materials" ? "Materials" : "Labor"
          })),
          skipAutoEmail: true, // Always skip auto email when saving - email will be sent only via "Save and Send Email" option
        };
        
        
        let response;
        if (!invoiceId) {
          setLoading(true);
          response = await AxiosInstance.post(`/v1/invoice`, object);
        } else {
          response = await AxiosInstance.put(`/v1/invoice/${invoiceId}`, {
            invoiceData: object,
            items: validLineItems,
          });
        }
        
        // Handle duplicate invoice number error from backend
        if (response?.data?.statusCode === 400 && 
            response?.data?.message?.includes("already exists")) {
          showToast.error(response?.data?.message);
          setLoading(false);
          setLoader(false);
          return;
        }
        
        if (response?.data?.statusCode === 200) {
          setSubmitAttempted(false);
          // Update invoiceData with the saved invoice data
          const savedInvoiceId = response?.data?.InvoiceId || location?.state?.invoiceId;
          if (savedInvoiceId) {
            if (response?.data?.data) {
              setInvoiceData(response?.data?.data);
            } else if (!invoiceData?.InvoiceId) {
              // If we don't have full invoice data, at least set the InvoiceId and preserve CustomerId
              setInvoiceData(prev => ({
                ...(prev || {}),
                InvoiceId: savedInvoiceId,
                CustomerId: prev?.CustomerId || values?.CustomerId || invoiceData?.CustomerId,
                customer: prev?.customer || invoiceData?.customer || (prev?.CustomerId ? {
                  CustomerId: prev?.CustomerId || values?.CustomerId,
                  EmailAddress: prev?.customer?.EmailAddress || invoiceData?.customer?.EmailAddress
                } : null)
              }));
            } else {
              // Update existing invoiceData with new InvoiceId if it changed, preserve CustomerId
              setInvoiceData(prev => ({
                ...(prev || {}),
                InvoiceId: savedInvoiceId,
                CustomerId: prev?.CustomerId || values?.CustomerId || invoiceData?.CustomerId
              }));
            }
          }

          setTimeout(() => {
            showToast.success(response?.data?.message);
          }, 500);

          const isWorker = tokenDecode?.Role === "Worker";

          // Check if we should open email dialog after save
          if (shouldOpenEmailAfterSave) {
            setShouldOpenEmailAfterSave(false);
            setMail(true);
            // Don't navigate, let user send email first
            return;
          }

          if (isCollect) {
            navigate(
              CompanyUrl
                ? `/${CompanyUrl}${
                    isWorker
                      ? "/staff-member/invoice-payment"
                      : "/invoice-payment"
                  }`
                : "/invoice-payment",
              {
                state: {
                  navigats: [...location?.state?.navigats, "/invoice-payment"],
                  id: response?.data?.InvoiceId,
                },
              }
            );
          } else {
            navigate(
              CompanyUrl && isWorker
                ? `/${CompanyUrl}/staff-member/worker-invoice`
                : CompanyUrl
                ? `/${CompanyUrl}/invoice`
                : "/worker-invoice",
              {
                replace: true,
                state: {
                  navigats: [
                    "/worker-invoice",
                    CompanyUrl && isWorker
                      ? `/${CompanyUrl}/staff-member/worker-invoice`
                      : CompanyUrl
                      ? "/invoice"
                      : "/worker-invoice",
                  ],
                },
              }
            );
          }
        } else {
          setTimeout(() => {
            showToast.error(response?.data?.message);
          }, 500);
        }
      } catch (error) {
        console.error("Error: ", error);
        if (error?.response?.data?.statusCode === 400) {
          const errorMessage = error?.response?.data?.message;
          if (Array.isArray(errorMessage)) {
            showToast.warning(errorMessage[0]);
          } else if (typeof errorMessage === 'string' && errorMessage.includes("already exists")) {
            showToast.error(errorMessage);
          } else {
            showToast.warning(errorMessage);
          }
        } else {
          showToast.error("An error occurred while submitting the form.");
        }
        setSubmitAttempted(false);
      } finally {
        setLoader(false);
        setLoading(false);
        // Reset the flag in case of error
        if (shouldOpenEmailAfterSave) {
          setShouldOpenEmailAfterSave(false);
        }
      }
    },
  });

  useEffect(() => {
    appliedInvoiceSettingsDefaults.current = false;
  }, [location.key]);

  useEffect(() => {
    // Do not use location.state.id (contract id) here — new invoices often open with
    // ContractId set; we still want company Invoice Settings (e.g. Net 15) until contract fetch overwrites.
    // Quick invoice still passes quickInvoiceData but should get the same default terms/subject.
    const skipCompanyInvoiceDefaults =
      Boolean(location?.state?.invoiceId) ||
      Boolean(location?.state?.formData);
    if (skipCompanyInvoiceDefaults || appliedInvoiceSettingsDefaults.current) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await AxiosInstance.get("/v1/invoice-settings");
        if (cancelled || res?.data?.statusCode !== 200 || !res?.data?.data) return;
        appliedInvoiceSettingsDefaults.current = true;
        const d = res.data.data;
        const term =
          normalizeInvoicePaymentDue(d.defaultNetTerms) ||
          d.defaultNetTerms ||
          "Net 30";
        formik.setFieldValue("PaymentDue", term);
        const issueForDue =
          formik.values.IssueDate ||
          IssueDate ||
          new Date().toISOString().split("T")[0];
        const days = netTermDays(term);
        if (days > 0) {
          setDueDate(addDaysFromIssueDateString(issueForDue, days));
        } else {
          setDueDate(issueForDue);
        }
        const subj = (d.defaultSubject || "").trim();
        if (subj) formik.setFieldValue("Subject", subj);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- blank new invoice: apply company invoice settings once per navigation
  }, [location.key]);

  useEffect(() => {
    if (location?.state?.id || location?.state?.invoiceId || location?.state?.quickInvoiceData || location?.state?.formData) {
      setLoader(true);

      if (
        location?.state?.selectedVisits &&
        location?.state?.selectedVisits.length > 0
      ) {
        setSelectedVisits(location?.state?.selectedVisits);
      }

      // Handle quote data (coming from quote generation)
      if (location?.state?.formData && location?.state?.QuoteId) {
        const quoteData = location?.state?.formData;
        
        formik.setValues((values) => ({
          ...values,
          Subject: quoteData?.Title || quoteData?.Subject || "Invoice from Quote",
          CustomerId: location?.state?.CustomerId || quoteData?.CustomerId,
          LocationId: location?.state?.LocationId || quoteData?.LocationId,
          CustomerMessage: quoteData?.CustomerMessage || "",
          PaymentDue:
            normalizeInvoicePaymentDue(quoteData?.PaymentDue) ||
            quoteData?.PaymentDue ||
            "Upon Receipt",
          CustomDays: quoteData?.CustomDays || "",
          Message: quoteData?.Message || "",
          Notes: quoteData?.Notes || "",
          Discount: quoteData?.Discount || 0,
          Tax: quoteData?.Tax || 0,
          subTotal: quoteData?.SubTotal || quoteData?.subTotal || 0,
          Total: quoteData?.Total || 0,
          // Add customer name fields
          Firstname: quoteData?.customerData?.FirstName || "",
          LastName: quoteData?.customerData?.LastName || "",
        }));

        // Set line items from quote products
        if (quoteData?.products && quoteData.products.length > 0) {
         
          
          const lineItemsFromQuote = quoteData.products.map(product => ({
            Description: product?.Description || product?.Name || "",
            Name: product?.Name || "",
            Type: product?.Type || "Material and Labor",
            ProductId: product?.ProductId || null, // Preserve ProductId from quote item
            Unit: product?.Unit || product?.Hourly || product?.Fixed || product?.Square || "1",
            Attachment: product?.Attachment || "",
            CostPerUnit: product?.CostPerUnit || product?.CostPerHour || product?.CostPerFixed || product?.CostPerSquare || 0,
            Hourly: product?.Hourly || "",
            CostPerHour: product?.CostPerHour || 0,
            Fixed: product?.Fixed || "",
            CostPerFixed: product?.CostPerFixed || 0,
            Square: product?.Square || "",
            CostPerSquare: product?.CostPerSquare || 0,
            Total: product?.Total || 0,
            // Ensure IsNonTaxable is set (default to false if not present)
            IsNonTaxable: product?.IsNonTaxable !== undefined ? product.IsNonTaxable : false,
            isNew: false,
          }));
          

          
          setLineItems(lineItemsFromQuote);
        }

        // Set invoiceData for quote data
        setInvoiceData({
          ...quoteData,
          customer: quoteData?.customerData,
          location: quoteData?.locationData,
          Items: quoteData?.products || [],
        });

        // Get invoice number
        const getNumber = async () => {
          try {
            const res = await AxiosInstance.get(`/v1/invoice/get_number`);
            if (res.data?.statusCode === 200) {
              const invoiceNumber = res.data?.invoiceNumber;
              if (!isNaN(invoiceNumber)) {
                formik.setValues((prevValues) => ({
                  ...prevValues,
                  InvoiceNumber: invoiceNumber,
                }));
              }
            }
          } catch (error) {
            console.error("Error fetching invoice number:", error);
          }
        };
        getNumber();
        setLoader(false);
        return;
      }

      // Handle quick invoice data
      if (location?.state?.quickInvoiceData) {
        const quickData = location?.state?.quickInvoiceData;
        formik.setValues((values) => ({
          ...values,
          Subject: "",
          CustomerId: location?.state?.customerId,
          LocationId: location?.state?.locationId,
        }));
        // Still get the invoice number for quick invoices
        const getNumber = async () => {
          try {
            const res = await AxiosInstance.get(`/v1/invoice/get_number`);
            if (res.data?.statusCode === 200) {
              const invoiceNumber = res.data?.invoiceNumber;
              if (!isNaN(invoiceNumber)) {
                formik.setValues((prevValues) => ({
                  ...prevValues,
                  InvoiceNumber: invoiceNumber,
                }));
              }
            }
          } catch (error) {
            console.error("Error fetching invoice number:", error);
          }
        };
        getNumber();
        setLoader(false);
        return;
      }

      const fetchData = async () => {
        try {
          var url;
          if (location?.state?.id) {
            url = `/v1/contract/contract_details/${location?.state?.id}`;
          } else {
            url = `/v1/invoice/invoice_detail/${location?.state?.invoiceId}`;
          }

          const res = await AxiosInstance.get(url);
          
          if (res.data?.statusCode === 200) {
            const data = res.data?.data;
            const isEditInvoice = Boolean(location?.state?.invoiceId);
            formik.setValues((values) => {
              const apiDue = data?.PaymentDue;
              const hasExplicitPaymentDue =
                apiDue != null && String(apiDue).trim() !== "";
              // Contract details API does not include PaymentDue — do not fall back to "Upon Receipt"
              // on new invoices; keep company Invoice Settings (e.g. Net 15) already on the form.
              const paymentDue = isEditInvoice
                ? normalizeInvoicePaymentDue(apiDue) ||
                  apiDue ||
                  "Upon Receipt"
                : hasExplicitPaymentDue
                  ? normalizeInvoicePaymentDue(apiDue) || apiDue
                  : values.PaymentDue || "Upon Receipt";

              return {
                ...values,
                Subject: data?.Subject || data?.Title,
                Firstname: data?.customer?.Firstname,
                LastName: data?.customer?.LastName,
                CustomerId: data?.CustomerId,
                CompanyId: data.CompanyId,
                LocationId: data?.LocationId,
                CustomerMessage: data?.CustomerMessage,
                PaymentDue: paymentDue,
                CustomDays: data?.CustomDays || "",
                DueDate: data?.DueDate,
                Message: data.Message,
                Notes: data?.Notes,
                Attachment: data?.Attachment,
                Discount: data?.Discount,
                Tax: data?.Tax,
                subTotal: data?.subTotal,
                Total: data?.Total,
                IssueDate: data?.IssueDate,
                InvoiceNumber: data?.InvoiceNumber || values?.InvoiceNumber,
              };
            });
            if (isEditInvoice) {
              setDueDate(data?.DueDate);
            } else if (data?.DueDate) {
              setDueDate(data.DueDate);
            }
            setInvoiceData(data);

            if (location?.state?.invoiceId) {
              setOriginalFormData({
                Subject: data?.Subject || data?.Title,
                Message: data.Message,
                Notes: data?.Notes,
                ContractDisclaimer:
                  data?.ContractDisclaimer ||
                  "Contract / Disclaimer\nThis invoice is valid for 30 days from the issue date. After this period, prices and terms may be subject to change.",
                Discount: data?.Discount,
                Tax: data?.Tax,
                PaymentDue:
                  normalizeInvoicePaymentDue(data?.PaymentDue) ||
                  data?.PaymentDue ||
                  "Upon Receipt",
                CustomDays: data?.CustomDays || "",
                Attachment: data?.Attachment || [],
              });
            }

            // Map items to ensure IsNonTaxable and ProductId are always present
            let baseLineItems = (data?.Items || [
              {
                Description: "",
                Name: "",
                Type: "",
                Unit: "",
                Attachment: "",
                CostPerUnit: "",
                Hourly: "",
                CostPerHour: "",
                Fixed: "",
                CostPerFixed: "",
                Square: "",
                CostPerSquare: "",
                Total: "",
                isNew: true,
              },
            ]).map(item => {
              
              
              return {
                ...item,
                // Preserve ProductId from contract item
                ProductId: item?.ProductId || null,
                // Ensure IsNonTaxable is set (default to false if not present)
                IsNonTaxable: item?.IsNonTaxable !== undefined ? item.IsNonTaxable : false,
              };
            });
            
           

            if (
              location?.state?.selectedVisits &&
              location?.state?.selectedVisits.length > 0
            ) {
              const selectedVisitsCount =
                location?.state?.selectedVisits.length;
              let multipliedLineItems = [];

              for (let i = 0; i < selectedVisitsCount; i++) {
                const visitInfo = location?.state?.selectedVisits[i];
                const visitDate = visitInfo?.StartDate
                  ? formatDateWithUserTimezone(visitInfo.StartDate, {
                      dateFormat: "MM/DD/YYYY",
                      includeSeconds: false
                    })
                  : "N/A";

                baseLineItems.forEach((item) => {
                  multipliedLineItems.push({
                    ...item,
                    Description: item.Description
                      ? `${item.Description} (Visit: ${visitDate})`
                      : `Visit: ${visitDate}`,
                    VisitId: visitInfo?.VisitId,
                    VisitDate: visitDate,
                    // Preserve ProductId when multiplying for visits
                    ProductId: item?.ProductId || null,
                    // Ensure IsNonTaxable is preserved when multiplying for visits
                    IsNonTaxable: item?.IsNonTaxable !== undefined ? item.IsNonTaxable : false,
                  });
                });
              }

              setLineItems(multipliedLineItems);

              if (location?.state?.invoiceId) {
                setOriginalLineItems(deepSnapshotLineItems(multipliedLineItems));
              }
            } else {
              setLineItems(baseLineItems);

              if (location?.state?.invoiceId) {
                setOriginalLineItems(deepSnapshotLineItems(baseLineItems));
              }
            }
          }
        } catch (error) {
          console.error("Error: ", error);
        } finally {
          setLoader(false);
        }
      };

      const getNumber = async () => {
        try {
          if (!location.state.invoiceId) {
            const res = await AxiosInstance.get(`/v1/invoice/get_number`);

            if (res.data?.statusCode === 200) {
              const invoiceNumber = res.data?.invoiceNumber;
              if (!isNaN(invoiceNumber)) {
                const nextQuoteNumber = invoiceNumber;
                formik.setValues((prevValues) => ({
                  ...prevValues,
                  InvoiceNumber: nextQuoteNumber,
                }));
              } else {
                console.error("Error: InvoiceNumber is not a valid number");
              }
            } else {
              console.error("API response error:", res?.data);
            }
          }
        } catch (error) {
          console.error("Error fetching invoice number:", error);
        }
      };

      const initialize = async () => {
        await getNumber();
        if (location?.state?.previewData) {
          formik.setValues(location?.state?.previewData?.values);
          setLineItems(
            location?.state?.previewData?.lineItems || [
              {
                Subject: "",
                Description: "",
                Name: "",
                Type: "",
                Unit: "",
                Attachment: "",
                CostPerUnit: "",
                Hourly: "",
                CostPerHour: "",
                Fixed: "",
                CostPerFixed: "",
                Square: "",
                CostPerSquare: "",
                Total: "",
                isNew: true,
                subTotal: "",
                IssueDate: "",
              },
            ]
          );
          formik.setFieldValue("CustomerId", location?.state?.CustomerId);
          formik.setFieldValue("LocationId", location?.state?.LocationId);
          window.history.replaceState(
            {
              id: location?.state?.previewData?.id
                ? location?.state?.previewData?.id
                : null,
            },
            ""
          );
        } else if (location?.state?.id) {
          await fetchData();
        }
      };

      initialize();
      fetchData();
      return () => {
        formik.resetForm();
        setLineItems([
          {
            FirstName: "",
            Subject: "",
            LastName: "",
            Description: "",
            Name: "",
            Type: "",
            Unit: "",
            Attachment: "",
            CostPerUnit: "",
            Hourly: "",
            CostPerHour: "",
            Fixed: "",
            CostPerFixed: "",
            Square: "",
            CostPerSquare: "",
            Total: "",
            isNew: true,
            subTotal: "",
            IssueDate: "",
          },
        ]);
      };
    }
  }, [location, tokenDecode]);

  const handleSelectChange = (index, selectedOption) => {
    // If NY surcharge + Percentage is active, inflate catalog price and Total
    // so each line already includes the surcharge — line items then sum naturally
    // to an inflated Subtotal, no bottom-added surcharge is needed.
    const inflate = (n) =>
      Number(((Number(n) || 0) * nySurchargeMultiplier).toFixed(2));

    const inflatedCostPerUnit = selectedOption?.CostPerUnit
      ? inflate(selectedOption.CostPerUnit)
      : selectedOption?.CostPerUnit;
    const inflatedCostPerSquare = selectedOption?.CostPerSquare
      ? inflate(selectedOption.CostPerSquare)
      : selectedOption?.CostPerSquare;
    const inflatedCostPerHour = selectedOption?.CostPerHour
      ? inflate(selectedOption.CostPerHour)
      : selectedOption?.CostPerHour;
    const inflatedCostPerFixed = selectedOption?.CostPerFixed
      ? inflate(selectedOption.CostPerFixed)
      : selectedOption?.CostPerFixed;

    const newLineItems = [...lineItems];
    newLineItems[index] = {
      ...newLineItems[index],
      ...selectedOption,
      // Preserve ProductId when selecting from Materials & Labor dropdown
      ProductId: selectedOption?.ProductId || selectedOption?.value || null,
      IsNonTaxable: selectedOption?.IsNonTaxable !== undefined ? selectedOption.IsNonTaxable : false,
      // Overwrite the spread Cost* values with the inflated equivalents so state
      // matches the (inflated) Total and the UI shows the card-price on the line.
      CostPerUnit: inflatedCostPerUnit,
      CostPerSquare: inflatedCostPerSquare,
      CostPerHour: inflatedCostPerHour,
      CostPerFixed: inflatedCostPerFixed,

      Total: selectedOption?.Unit
        ? Number(selectedOption?.Unit || 0) * Number(inflatedCostPerUnit || 0)
        : selectedOption?.Square
        ? Number(selectedOption?.Square || 0) * Number(inflatedCostPerSquare || 0)
        : selectedOption?.Hourly
        ? Number(selectedOption?.Hourly || 0) * Number(inflatedCostPerHour || 0)
        : Number(selectedOption?.Fixed || 0) * Number(inflatedCostPerFixed || 0),
    };
    setLineItems(newLineItems);
    setMenuIsOpen((prevState) => {
      const newState = [...prevState];
      newState[index] = false;
      return newState;
    });
  };

  const addLineItem = () => {
    const newItem = {
      Description: "",
      Name: "",
      Attachment: "",
      Type: "",
      Unit: "",
      CostPerUnit: "",
      Hourly: "",
      CostPerHour: "",
      Fixed: "",
      CostPerFixed: "",
      Square: "",
      CostPerSquare: "",
      Total: "",
      isNew: true,
    };
    setLineItems([...lineItems, newItem]);
    // Reset submitAttempted when adding a new line item so new items don't show red borders immediately
    setSubmitAttempted(false);
  };
  const deleteLineItem = (index) => {
    const newLineItems = lineItems?.filter((_, i) => i !== index);
    setLineItems(newLineItems);
  };
  const [showTax, setShowTax] = useState(false);
  const calculateSubTotal = () => {
    const Total = lineItems?.reduce(
      (sum, item) => sum + Number(item?.Total || 0),
      0
    );
    return Total;
  };

  const subTotal = calculateSubTotal();

  const [calculatedTaxes, setCalculatedTaxes] = useState({
    totalTaxes: 0,
    taxDetails: [],
    materialSubTotal: 0,
    laborSubTotal: 0
  });

  // When merchant uses the NY-surcharge pricing structure, discounts are suppressed
  // and each line item's price is inflated at catalog-pick time (see handleSelectChange)
  // so line-item Totals already include the surcharge. Subtotal therefore sums the
  // already-inflated line Totals and Total = Subtotal + Tax (no bottom-added surcharge).
  const isNySurcharge =
    cardFeeConfig?.PricingStructure === "Surcharge (New York)" &&
    Number(cardFeeConfig?.FeeAmount) > 0;

  // Multiplier used to inflate catalog prices/totals at pick-time for NY surcharge.
  // Only Percentage fee type affects per-line price inflation; Fixed fees are flat and
  // don't distribute across line items — fall back to end-add behavior for Fixed.
  const nySurchargeMultiplier =
    isNySurcharge && cardFeeConfig?.FeeType === "Percentage"
      ? 1 + Number(cardFeeConfig.FeeAmount) / 100
      : 1;
  const nySurchargeFixedAmount =
    isNySurcharge && cardFeeConfig?.FeeType === "Fixed"
      ? Number(cardFeeConfig.FeeAmount) || 0
      : 0;

  const discountAmount = isNySurcharge
    ? 0
    : formik?.values?.Discount
    ? (Number(formik?.values?.Discount) * subTotal) / 100
    : 0;
  const discountedTotal = Math.max(0, subTotal - discountAmount);
  const taxAmount = formik?.values?.Tax
    ? (Number(formik?.values?.Tax) * discountedTotal) / 100
    : 0;

  // Include calculated taxes from API in the total. For Fixed NY fees we still add
  // the flat amount once at the end (can't distribute across lines).
  const totalWithCalculatedTaxes =
    discountedTotal +
    (calculatedTaxes?.totalTaxes || 0) +
    taxAmount +
    nySurchargeFixedAmount;
  const Total = totalWithCalculatedTaxes?.toFixed(2);

  const [mail, setMail] = useState(false);
  
  // Save invoice function that can be called from InvoiceMail
  const saveInvoiceForEmail = async () => {
    try {
      const values = formik.values;
      
      // Validate invoice number before saving
      const invoiceNumber = values?.InvoiceNumber;
      if (!invoiceNumber || invoiceNumber <= 0) {
        showToast.error("Please enter a valid invoice number.");
        throw new Error("Invalid invoice number");
      }

      // Check if invoice number exists (only if number was changed or it's a new invoice)
      const companyId = getItem("CompanyId") || tokenDecode?.companyId;
      const invoiceId = location?.state?.invoiceId;
      const isNewInvoice = !invoiceId;
      const invoiceNumberChanged = invoiceId && 
        String(invoiceNumber) !== String(invoiceData?.InvoiceNumber);

      if (isNewInvoice || invoiceNumberChanged) {
        try {
          const checkPayload = {
            InvoiceNumber: Number(invoiceNumber),
          };
          
          // If editing, exclude current invoice from check
          if (invoiceId) {
            checkPayload.InvoiceId = invoiceId;
          }

          const checkRes = await AxiosInstance.post(`/v1/invoice/check_number`, checkPayload);

          if (checkRes?.data?.statusCode === 203) {
            showToast.error(
              "Invoice number already exists. Please choose a different number."
            );
            throw new Error("Invoice number already exists");
          }
        } catch (error) {
          if (error?.response?.data?.statusCode === 203) {
            showToast.error(
              "Invoice number already exists. Please choose a different number."
            );
            throw new Error("Invoice number already exists");
          }
          // If it's a different error, continue with save (backend will also validate)
        }
      }

      setLoading(true);
      const strings = [];
      for (let item of values?.Attachment) {
        if (typeof item !== "string") {
          const string = await postFile(item);
          strings.push(string);
        } else {
          strings.push(item);
        }
      }
      // Filter out empty line items (items without Name/ProductId selected)
      const validLineItems = (lineItems || []).filter(item => 
        item?.Name && item?.Name.trim() !== "" && 
        (item?.ProductId || item?.Name)
      );

      // Validate that there is at least one valid line item
      if (validLineItems.length === 0) {
        showToast.error("Please select at least one material or labor item before saving.");
        setLoading(false);
        throw new Error("No valid line items");
      }

      for (let item of validLineItems) {
        if (typeof item?.Attachment !== "string") {
          const string = await postFile(item?.Attachment);
          item.Attachment = string;
        }
      }

      const object = {
        ...values,
        items: validLineItems,
        subTotal: validLineItems?.reduce(
          (sum, item) => Number(sum) + Number(item?.Total || 0) || 0,
          0
        ),
        Total: Total,
        Attachment: strings,
        Role: tokenDecode,
        IssueDate: IssueDate,
        DueDate: DueDate,
        ContractId: location?.state?.id,
        AddedAt: new Date(),
        CompanyId:
          getItem("CompanyId") || tokenDecode?.companyId,
        selectedVisits: location?.state?.selectedVisits || [],
        // Include tax calculation details
        MaterialTotal: calculatedTaxes.materialSubTotal,
        LaborTotal: calculatedTaxes.laborSubTotal,
        TaxableAmount: discountedTotal,
        TotalTax: calculatedTaxes.totalTaxes,
        CalculatedTaxes: calculatedTaxes.taxDetails.map(tax => ({
          ...tax,
          appliedTo: tax.type === "Materials" ? "Materials" : "Labor"
        })),
        skipAutoEmail: true, // Always skip auto email when saving - email will be sent only via "Save and Send Email" option
      };
      
      let response;
      if (!invoiceId) {
        response = await AxiosInstance.post(`/v1/invoice`, object);
      } else {
        response = await AxiosInstance.put(`/v1/invoice/${invoiceId}`, {
          invoiceData: object,
          items: validLineItems,
        });
      }
      
      // Handle duplicate invoice number error from backend
      if (response?.data?.statusCode === 400 && 
          response?.data?.message?.includes("already exists")) {
        showToast.error(response?.data?.message);
        throw new Error(response?.data?.message);
      }
      
      if (response?.data?.statusCode === 200) {
        // Update invoiceData with the saved invoice data
        const savedInvoiceId = response?.data?.InvoiceId || location?.state?.invoiceId;
        if (savedInvoiceId) {
          if (response?.data?.data) {
            setInvoiceData(response?.data?.data);
          } else if (!invoiceData?.InvoiceId) {
            // If we don't have full invoice data, at least set the InvoiceId and preserve CustomerId
            setInvoiceData(prev => ({
              ...(prev || {}),
              InvoiceId: savedInvoiceId,
              CustomerId: prev?.CustomerId || values?.CustomerId || invoiceData?.CustomerId,
              customer: prev?.customer || invoiceData?.customer || (prev?.CustomerId ? {
                CustomerId: prev?.CustomerId || values?.CustomerId,
                EmailAddress: prev?.customer?.EmailAddress || invoiceData?.customer?.EmailAddress
              } : null)
            }));
          } else {
            // Update existing invoiceData with new InvoiceId if it changed, preserve CustomerId
            setInvoiceData(prev => ({
              ...(prev || {}),
              InvoiceId: savedInvoiceId,
              CustomerId: prev?.CustomerId || values?.CustomerId || invoiceData?.CustomerId
            }));
          }
        }
        
        return {
          success: true,
          invoiceId: savedInvoiceId,
          invoiceData: response?.data?.data || invoiceData,
          message: response?.data?.message
        };
      } else {
        throw new Error(response?.data?.message || "Failed to save invoice");
      }
    } catch (error) {
      console.error("Error saving invoice: ", error);
      if (error?.response?.data?.statusCode === 400) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          showToast.warning(errorMessage[0]);
        } else if (typeof errorMessage === 'string' && errorMessage.includes("already exists")) {
          showToast.error(errorMessage);
        } else {
          showToast.warning(errorMessage);
        }
      } else if (error?.message) {
        // Error already handled above
      } else {
        showToast.error("An error occurred while saving the invoice.");
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  const handleInvoiceNumberChange = async () => {
    const enteredInvoiceNumber = Number(formik?.values?.InvoiceNumber);
    const companyId =
      getItem("CompanyId") || tokenDecode?.CompanyId;
    
    if (!enteredInvoiceNumber || enteredInvoiceNumber <= 0) {
      showToast.error("Please enter a valid invoice number.");
      return;
    }

    try {
      setLoading(true);
      const checkPayload = {
        InvoiceNumber: enteredInvoiceNumber,
      };
      
      // If editing an existing invoice, exclude it from the check
      if (location?.state?.invoiceId) {
        checkPayload.InvoiceId = location.state.invoiceId;
      }

      const res = await AxiosInstance.post(`/v1/invoice/check_number`, checkPayload);

      if (res?.data?.statusCode === 200) {
        setTimeout(() => {
          showToast.success("Invoice number is valid and added successfully.");
        }, 500);
        setIsNumChange(false);
      } else if (res?.data?.statusCode === 203) {
        setTimeout(() => {
          showToast.error(
            "Invoice number already exists. Please choose a different number."
          );
        }, 500);
        // Don't clear the field, let user see what they entered
        setIsNumChange(true);
      } else {
        setTimeout(() => {
          showToast.error("Failed to check Invoice number. Please try again.");
        }, 500);
      }
    } catch (error) {
      console.error(
        "Error checking Invoice number: ",
        error?.response || error?.message
      );
      if (error?.response?.data?.statusCode === 203) {
        showToast.error(
          "Invoice number already exists. Please choose a different number."
        );
      } else {
        setTimeout(() => {
          showToast.error("An error occurred while checking the Invoice number.");
        }, 500);
      }
    } finally {
      setLoading(false);
    }
  };
  const handlePaymentDueChange = (e) => {
    const selectedValue = e.target.value;
    formik.setFieldValue("PaymentDue", selectedValue);

    if (selectedValue === INVOICE_PAYMENT_DUE_CUSTOM) {
      return;
    }

    const daysToAdd = netTermDays(selectedValue);
    const issueForDue =
      formik.values.IssueDate || IssueDate || new Date().toISOString().split("T")[0];

    if (daysToAdd > 0) {
      setDueDate(addDaysFromIssueDateString(issueForDue, daysToAdd));
    } else if (selectedValue === "Upon Receipt") {
      setDueDate(issueForDue);
    }
  };

  const handleCustomDaysChange = (e) => {
    const customDays = parseInt(e.target.value, 10);
    formik.setFieldValue("CustomDays", customDays);

    if (customDays && customDays > 0) {
      const issueForDue =
        formik.values.IssueDate || IssueDate || new Date().toISOString().split("T")[0];
      setDueDate(addDaysFromIssueDateString(issueForDue, customDays));
    }
  };

  const handleClick = (event) => {
    handleNavigate();
  };
  const handleNavigate = () => {
    navigate(
      `/${
        tokenDecode?.Role === "Worker"
          ? `${CompanyUrl}/staff-member/worker-invoice`
          : `${CompanyUrl}/invoice`
      }`,
      {
        state: {
          navigats: location?.state?.navigats?.slice(0, -1) || [],
        },
      }
    );
  };

  // Handle calculated taxes from DiscountTable
  const handleTaxesCalculated = (taxData) => {
    setCalculatedTaxes(taxData);
  };

  return (
    <>
      <AddInvoice
        invoiceData={invoiceData}
        subTotal={subTotal}
        formik={formik}
        loading={loading}
        isNumChange={isNumChange}
        setIsNumChange={setIsNumChange}
        handleInvoiceNumberChange={handleInvoiceNumberChange}
        IssueDate={IssueDate}
        setIssueDate={setIssueDate}
        setDueDate={setDueDate}
        DueDate={DueDate}
        loader={loader}
        handlePaymentDueChange={handlePaymentDueChange}
        handleCustomDaysChange={handleCustomDaysChange}
        lineItems={lineItems}
        handleSelectChange={handleSelectChange}
        setLineItems={setLineItems}
        setLoader={setLoader}
        menuIsOpen={menuIsOpen}
        setMenuIsOpen={setMenuIsOpen}
        deleteLineItem={deleteLineItem}
        showCosts={showCosts}
        setShowCosts={setShowCosts}
        addLineItem={addLineItem}
        discountAmount={discountAmount}
        showTax={showTax}
        setShowTax={setShowTax}
        taxAmount={taxAmount}
        Total={Total}
        dropdownOpen={dropdownOpen}
        toggle={toggle}
        mail={mail}
        setMail={setMail}
        setIsCollect={setIsCollect}
        setShouldOpenEmailAfterSave={setShouldOpenEmailAfterSave}
        CompanyUrl={CompanyUrl}
        setLoading={setLoading}
        selectedVisits={selectedVisits}
        handleClick={handleClick}
        handleNavigate={handleNavigate}
        tokenDecode={tokenDecode}
        isDonationMode={isDonationMode}
        handleTaxesCalculated={handleTaxesCalculated}
        quickInvoiceData={location?.state?.quickInvoiceData}
        isQuickInvoice={location?.state?.quickInvoiceData?.isQuickInvoice}
        invoiceId={location?.state?.invoiceId}
        saveInvoiceForEmail={saveInvoiceForEmail}
        calculatedTaxes={calculatedTaxes}
        submitAttempted={submitAttempted}
        setSubmitAttempted={setSubmitAttempted}
        touchedLineItems={touchedLineItems}
        setTouchedLineItems={setTouchedLineItems}
        isNySurcharge={isNySurcharge}
        cardFeeConfig={cardFeeConfig}
        surchargeDisclaimer={surchargeDisclaimer}
      />
    </>
  );
};

export default InvoiceDetails;
